import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  booleanAttribute,
  inject,
} from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ClassService } from '../../services/class.service';
import { ClassItem } from '../../interfaces/class.interface';
import { Observable, Subscription, combineLatest, forkJoin } from 'rxjs';
import { WidgetService } from '../../services/widget.service';
import { WidgetResponse } from '../../interfaces/widget.interface';
import { ClassDate } from '../../interfaces/date.interface';
import { DialogService } from '../../services/dialog.service';
import { BookingConfirmationComponent } from './booking-confirmation/booking-confirmation.component';
import { SubscriptionService, SubscriptionCheckResponse } from '../../services/subscription.service';
import { ScheduleService } from '../../services/schedule.service';
import { firstValueFrom } from 'rxjs';
import { interval } from 'rxjs';
import { UtilsService } from '../../services/utils.service';
import { WidgetTheme, lightTheme, darkTheme } from '../../themes/widget.theme';
import { LoginComponent } from '../login/login.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

interface MappedTenant {
  id: string;
  tenant_name: string;
  is_selected: boolean;
}

@Component({
    selector: 'ufp-class-booking',
    templateUrl: './class-booking.component.html',
    styleUrls: ['./class-booking.component.css'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class ClassBookingComponent implements OnInit, OnDestroy {
  @Input() widgetId: string = '';
  @Input() base_api_uri?: string;
  @Input() set theme(value: 'light' | 'dark' | undefined) {
    this._theme = value || 'light';
    this.updateTheme();
  }
  get theme(): 'light' | 'dark' {
    return this._theme;
  }

  @Input() set primaryColor(value: string | undefined) {
    if (value) {
      this._customTheme = { ...this._customTheme, primaryColor: value };
      this.updateTheme();
    }
  }

  @Input() set backgroundColor(value: string | undefined) {
    if (value) {
      this._customTheme = { ...this._customTheme, backgroundColor: value };
      this.updateTheme();
    }
  }

  @Input() set textColor(value: string | undefined) {
    if (value) {
      this._customTheme = { ...this._customTheme, textColor: value };
      this.updateTheme();
    }
  }

  @Input() preferredAuth?: string;
  @Input({ transform: booleanAttribute }) allowBooking = true;

  private _theme: 'light' | 'dark' = 'light';
  private _customTheme: Partial<WidgetTheme> = {};
  currentTheme: WidgetTheme = lightTheme;
  
  dates: ClassDate[] = [];
  visibleDatesCount: number = 7; // Default, will be adjusted based on width
  selectedDateIndex = 0;
  dateScrollIndex = 0;
  private resizeObserver: ResizeObserver;
  classes: ClassItem[] = [];
  pendingBooking: ClassItem | null = null;
  loading = false;
  error = '';
  isLoggedIn$: Observable<boolean>;
  userName: string = '';
  userAvatar: string = '';
  userInitial: string = '';
  showUserMenu: boolean = false;
  private authSubscription?: Subscription;
  private viewportMeta: HTMLMetaElement | null = null;

  // Widget-related properties
  widgetConfig?: WidgetResponse;
  tenants: MappedTenant[] = [];
  selectedTenantUuid: string | null = null;
  showTenantDropdown = false;
  isInitialized = false;
  private bookedClassIds: string[] = [];
  private countdownSubscription?: Subscription;
  bookingTimers: { [key: string]: { isOpen: boolean; timeLeft: string } } = {};
  isLoadingStates = true;
  private subscriptions = new Subscription(); // Subscription container

  // Filter-related properties
  allClasses: ClassItem[] = []; // Store all classes before filtering
  classTypes: { id: number; name: string }[] = [];
  coaches: { id: number; name: string }[] = [];
  selectedTypeId: number | null = null;
  selectedCoachId: number | null = null;

  // Add this property
  private companyUuid: string | null = null;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  constructor(
    public authService: AuthService,
    private classService: ClassService,
    private widgetService: WidgetService,
    private elementRef: ElementRef,
    private dialogService: DialogService,
    private subscriptionService: SubscriptionService,
    private renderer: Renderer2,
    private scheduleService: ScheduleService,
    private utils: UtilsService
  ) {
    this.isLoggedIn$ = this.authService.isLoggedIn$;
    
    this.resizeObserver = new ResizeObserver(entries => {
      this.adjustVisibleDates(entries[0].contentRect.width);
      this.setupMobileViewport();
      this.notifyViewUpdate();
    });
  }

  /** HttpClient withFetch() callbacks can run outside NgZone; refresh the view explicitly. */
  private notifyViewUpdate(): void {
    this.ngZone.run(() => this.cdr.detectChanges());
  }

  trackByClassId(index: number, item: ClassItem): string {
    return item.uuid;
  }

  ngOnInit() {
    if (!this.widgetId) {
      console.error('Widget ID is required');
      return;
    }

    // Initialize widget and auth subscription
    this.initializeWidget();
    
    // Subscribe to auth state changes
    this.subscriptions.add(
      combineLatest([
        this.authService.isLoggedIn$,
        this.authService.userInfo$
      ]).subscribe(([isLoggedIn, userInfo]) => {
        if (isLoggedIn && userInfo) {
          this.userName = userInfo.client_name;
          this.userAvatar = userInfo.client_image || '';
          this.userInitial = userInfo.client_name.charAt(0).toUpperCase();
        } else {
          this.resetUserInfo();
        }
        this.notifyViewUpdate();
      })
    );

    // Observe element size
    this.resizeObserver.observe(this.elementRef.nativeElement);

    // Initial mobile viewport setup
    this.setupMobileViewport();
    this.loadBookedClasses();
    
    this.subscriptions.add(
      this.authService.isLoggedIn$.subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.loadBookedClasses();
        } else {
          this.bookedClassIds = [];
        }
      })
    );
    
    this.loadInitialData();
    this.updateTheme();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe(); // Clean up all subscriptions
    this.countdownSubscription?.unsubscribe();
    this.resizeObserver.disconnect();
    if (this.viewportMeta) {
      document.head.removeChild(this.viewportMeta);
    }
  }

  private setupMobileViewport() {
    // Check if viewport meta exists
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    
    // If no viewport meta exists, create one
    if (!viewportMeta) {
      this.viewportMeta = document.createElement('meta');
      this.viewportMeta.name = 'viewport';
      this.viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(this.viewportMeta);
    }

    // Get the widget's width
    const widgetWidth = this.elementRef.nativeElement.offsetWidth;
    
    // If widget width is less than typical mobile width (e.g., 480px)
    // and no viewport meta exists, we're probably in a mobile iframe
    if (widgetWidth < 480 && !viewportMeta) {
      const scale = widgetWidth / 375; // 375px is iPhone width
      this.renderer.setStyle(this.elementRef.nativeElement, 'transform', `scale(${scale})`);
      this.renderer.setStyle(this.elementRef.nativeElement, 'transform-origin', 'top left');
      this.renderer.setStyle(this.elementRef.nativeElement, 'width', `${375}px`);
      this.renderer.setStyle(this.elementRef.nativeElement, 'height', 'auto');
    } else {
      this.renderer.removeStyle(this.elementRef.nativeElement, 'transform');
      this.renderer.removeStyle(this.elementRef.nativeElement, 'transform-origin');
      this.renderer.removeStyle(this.elementRef.nativeElement, 'width');
      this.renderer.removeStyle(this.elementRef.nativeElement, 'height');
    }
  }

  private async initializeWidget() {
    try {
      const widgetDetails = await this.widgetService.getWidgetDetails(this.widgetId).toPromise();
      if (widgetDetails) {
        if (widgetDetails.widget_type !== 'ClassBooking') {
          throw new Error('Invalid widget type. Expected class-booking.');
        }

        this.widgetConfig = widgetDetails;
        this.companyUuid = widgetDetails.widget_company_uuid;
        
        // Map API response to our internal format
        this.tenants = widgetDetails.widget_enabled_tenants.map((tenant): MappedTenant => ({
          id: tenant.tenant_uuid,
          tenant_name: tenant.tenant_name,
          is_selected: false
        }));

        this.showTenantDropdown = this.tenants.length > 1;
        
        if (this.tenants.length > 0) {
          this.tenants[0].is_selected = true;
          this.selectedTenantUuid = this.tenants[0].id;
          
          this.loadDates();
          this.adjustVisibleDates(window.innerWidth);
          this.loadClasses();
          this.isInitialized = true;
        } else {
          throw new Error('No tenants available');
        }
      }
    } catch (error: any) {
      console.error('Failed to initialize widget:', error);
      this.error = 'Failed to initialize widget. Please try again later.';
      this.isInitialized = false;
    } finally {
      this.notifyViewUpdate();
    }
  }

  onTenantChange(value: string) {
    this.selectedTenantUuid = value;
    this.loadClasses();
  }

  private adjustVisibleDates(width: number) {
    const availableWidth = width - 144;
    this.visibleDatesCount = Math.max(Math.floor(availableWidth / 85), 4);
    
    // Set CSS variable for date widths
    this.elementRef.nativeElement.style.setProperty('--visible-dates', this.visibleDatesCount.toString());
    
    this.updateVisibleDates();
  }

  private loadDates() {
    const today = new Date();
    const dates: ClassDate[] = [];
    
    // Generate dates for the next 30 days
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setHours(0, 0, 0, 0); // Set time to start of day
      date.setDate(today.getDate() + i);
      
      dates.push({
        date: date,
        dayName: this.getDayName(date),
        dayNumber: date.getDate(),
        monthName: this.getMonthName(date),
        isVisible: i < this.visibleDatesCount
      });
    }
    
    this.dates = dates;
  }

  private getDayName(date: Date): string {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  private getMonthName(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'short' });
  }

  private updateVisibleDates() {
    // Update visibility flag for each date
    this.dates.forEach((date, index) => {
      date.isVisible = index >= this.dateScrollIndex && 
                      index < (this.dateScrollIndex + this.visibleDatesCount);
    });

    // Update transform for smooth sliding
    const dateContainer = this.elementRef.nativeElement.querySelector('.ufp-dates');
    if (dateContainer) {
      const translateX = -(this.dateScrollIndex * (100 / this.visibleDatesCount));
      dateContainer.style.transform = `translateX(${translateX}%)`;
    }
  }

  scrollDates(direction: 'prev' | 'next') {
    const currentIndex = this.dateScrollIndex;
    const scrollStep = Math.max(1, Math.floor(this.visibleDatesCount / 2));
    
    if (direction === 'next' && currentIndex + this.visibleDatesCount < this.dates.length) {
      this.dateScrollIndex = Math.min(
        currentIndex + scrollStep,
        this.dates.length - this.visibleDatesCount
      );
    } else if (direction === 'prev' && currentIndex > 0) {
      this.dateScrollIndex = Math.max(0, currentIndex - scrollStep);
    }

    this.updateVisibleDates();
  }

  selectDate(index: number) {
    if (this.selectedDateIndex === index) {
      return; // Don't reload if same date is selected
    }
    this.selectedDateIndex = index;
    this.loadClasses();
  }

  async processBooking(classItem: ClassItem, isWaitlist: boolean = false) {
    const result = await this.dialogService.open(BookingConfirmationComponent, {
      data: {
        classItem,
        tenantUuid: this.selectedTenantUuid,
        isWaitlist,
        androidAppLink: this.widgetConfig?.member_app_android_link || '',
        iosAppLink: this.widgetConfig?.member_app_ios_link || ''
      }
    });

    if (result) {
      // Refresh the classes after successful booking
      this.loadClasses();
    }
  }

  getButtonText(classItem: ClassItem): string {
    if (this.isLoadingStates) {
      return 'Loading...';
    }

    if (this.isClassBooked(classItem.uuid)) {
      return 'BOOKED';
    }

    if (!this.isBookingOpen(classItem)) {
      return this.getBookingTimeLeft(classItem);
    }
    
    if (classItem.total_booking >= classItem.max_capacity) {
      return 'WAITLIST';
    }

    // Check subscription status that was pre-loaded
    if (this.authService.isLoggedIn() && classItem.subscriptionStatus && !classItem.subscriptionStatus.canBook) {
      switch (classItem.behaviour.toLowerCase()) {
        case 'session_pack':
          return 'SESSION PACK REQUIRED';
        case 'membership_session_pack':
          return 'MEMBERSHIP REQUIRED';
        case 'members_only_session':
          return 'MEMBERSHIP REQUIRED';
        default:
          return 'SUBSCRIPTION REQUIRED';
      }
    }

    return 'BOOK';
  }

  getButtonTemplate(classItem: ClassItem): string {
    if (!this.isBookingOpen(classItem) && !this.isClassBooked(classItem.uuid)) {
      return `
        <div class="opens-in-text">
          <span class="opens-in-time">${this.getBookingTimeLeft(classItem)}</span>
          <span class="opens-in-label">Opens in</span>
        </div>
      `;
    }

    const buttonText = this.getButtonText(classItem);
    const subscriptionRequired = buttonText.includes('REQUIRED');
    const className = subscriptionRequired ? 'subscription-required' : '';
    
    return `<div class="button-text ${className}">${buttonText}</div>`;
  }

  async bookClass(classItem: ClassItem) {
    if (this.isClassBooked(classItem.uuid)) {
      return;
    }

    if (!this.isBookingOpen(classItem)) {
      return;
    }

    if (!this.authService.isLoggedIn()) {
      if (!this.companyUuid) {
        console.error('No company_uuid available for login');
        return;
      }

      this.pendingBooking = classItem;
      this.showLoginDialog();
      return;
    }

    await this.handleClassBooking(classItem);
  }

  private getSubscriptionErrorMessage(behaviour: string): string {
    switch (behaviour.toLowerCase()) {
      case 'session_pack':
        return 'You need an active session pack to join this class.';
      case 'membership_session_pack':
        return 'You need an active membership and session pack to join this class.';
      case 'members_only_session':
        return 'You need an active membership to join this class.';
      default:
        return 'You do not have the required subscription to join this class.';
    }
  }

  async onLoginSuccess() {
    // First load the booked classes
    await firstValueFrom(this.scheduleService.fetchSchedule());
    this.loadBookedClasses();
    
    // Then proceed with the pending booking if any
    if (this.pendingBooking) {
      const classItem = this.pendingBooking;
      this.pendingBooking = null;

      // Check if the class is already booked
      if (this.isClassBooked(classItem.uuid)) {
        const tenantId = this.widgetConfig?.widget_enabled_tenants.find(
          t => t.tenant_uuid === this.selectedTenantUuid
        )?.tenant_id;

        this.dialogService.open(BookingConfirmationComponent, {
          classItem,
          tenantUuid: this.selectedTenantUuid,
          tenantId,
          isWaitlist: false,
          alreadyBooked: true,
          androidAppLink: this.widgetConfig?.member_app_android_link || '',
          iosAppLink: this.widgetConfig?.member_app_ios_link || ''
        });
        return;
      }

      // Add a small delay to ensure the login dialog is fully closed
      setTimeout(() => {
        this.handleClassBooking(classItem);
      }, 100);
    }
  }

  loadClasses() {
    this.loading = true;
    this.error = '';

    const selectedDate = this.dates[this.selectedDateIndex].date;
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    // Widen the API window by one local day on each side so UTC-stored class times
    // near midnight (common for non-UTC clubs) are still returned, then filter below.
    const apiRangeStart = new Date(dayStart);
    apiRangeStart.setDate(apiRangeStart.getDate() - 1);
    const apiRangeEnd = new Date(dayEnd);
    apiRangeEnd.setDate(apiRangeEnd.getDate() + 1);

    const classesRequest = this.classService.getClasses(
      this.selectedTenantUuid!,
      apiRangeStart,
      apiRangeEnd
    );

    const scheduleRequest = this.scheduleService.fetchSchedule(apiRangeStart, apiRangeEnd);

    // Use forkJoin to make both requests in parallel
    forkJoin({
      classes: classesRequest,
      schedule: scheduleRequest
    }).subscribe({
      next: async (response) => {
        // Handle classes response
        this.allClasses = response.classes.items.map(item => {
          const localDate = this.parseUtcClassDateTime(item.start_datetime);

          return {
            ...item,
            original_start_datetime: item.start_datetime,
            start_datetime: localDate.toISOString(),
            local_start_datetime: localDate,
            subscriptionStatus: {
              canBook: true,
              message: undefined
            }
          };
        }).filter(item => {
          const now = new Date();
          return (
            item.local_start_datetime > now &&
            this.isSameLocalCalendarDay(item.local_start_datetime, dayStart)
          );
        }).sort((a, b) => {
          // Sort by local time ascending
          return a.local_start_datetime.getTime() - b.local_start_datetime.getTime();
        });

        // Update booked class IDs from schedule response
        this.bookedClassIds = response.schedule?.items?.classes
          ? response.schedule.items.classes
              .filter(booking => {
                const bookingDate = this.parseUtcClassDateTime(
                  booking.class_details.start_datetime,
                );
                return (
                  bookingDate >= dayStart &&
                  bookingDate < dayEnd &&
                  booking.status !== 'Cancelled'
                );
              })
              .map(booking => booking.class_details.uuid.toString())
          : [];
          
        // Check subscription status for each class
        if (this.authService.isLoggedIn()) {
          for (const classItem of this.allClasses) {
            try {
              const status = await firstValueFrom(this.subscriptionService.checkSubscriptionStatus(classItem));
              classItem.subscriptionStatus = {
                canBook: status.canBook ?? false,
                message: status.message
              };
            } catch (error) {
              console.error('Error checking subscription status:', error);
              classItem.subscriptionStatus = {
                canBook: false,
                message: 'Error checking subscription'
              };
            }
          }
        }

        this.updateFilters();
        this.applyFilters();
        this.loading = false;
        this.startCountdownTimer();
        this.notifyViewUpdate();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading classes:', error);
        
        // Handle specific error cases
        if (error === 'Please log in to view classes.') {
          this.showLoginDialog();
          this.error = 'Please log in to view classes';
        } else if (error?.message) {
          // If error has a message property, use it
          this.error = error.message;
        } else if (typeof error === 'string') {
          // If error is a string, use it directly
          this.error = error;
        } else {
          // Default error message
          this.error = 'Unable to load classes. Please try again later.';
        }
        this.notifyViewUpdate();
      }
    });
  }

  ConvertUTCToLocalDateTime(date: Date | string): Date {
    return this.parseUtcClassDateTime(date);
  }

  /** API class datetimes are UTC without a trailing Z. */
  private parseUtcClassDateTime(date: Date | string): Date {
    if (date instanceof Date) {
      return new Date(date.getTime());
    }
    const normalized =
      date.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(date) ? date : `${date}Z`;
    return new Date(normalized);
  }

  private isSameLocalCalendarDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }


  private updateFilters() {
    // Update class types filter
    this.classTypes = Array.from(
      new Map(
        this.allClasses.map(item => [
          item.type_id,
          { id: item.type_id, name: item.type_name_primary_language }
        ])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Update coaches filter
    this.coaches = Array.from(
      new Map(
        this.allClasses.map(item => [
          item.coach_id,
          { id: item.coach_id, name: item.coach_name }
        ])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));
  }

  private applyFilters() {
    let filteredClasses = [...this.allClasses];

    if (this.selectedTypeId) {
      filteredClasses = filteredClasses.filter(
        item => item.type_id === this.selectedTypeId
      );
    }

    if (this.selectedCoachId) {
      filteredClasses = filteredClasses.filter(
        item => item.coach_id === this.selectedCoachId
      );
    }

    this.classes = filteredClasses;
  }

  onTypeFilterChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedTypeId = select.value ? Number(select.value) : null;
    this.applyFilters();
  }

  onCoachFilterChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedCoachId = select.value ? Number(select.value) : null;
    this.applyFilters();
  }

  private resetUserInfo() {
    this.userName = '';
    this.userAvatar = '';
    this.userInitial = '';
    this.showUserMenu = false;
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
  }

  logout(event: Event): void {
    event.stopPropagation();
    this.authService.logout();
    this.showUserMenu = false;
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: Event): void {
    // Get the shadow root element
    const shadowRoot = this.elementRef.nativeElement.shadowRoot;
    if (!shadowRoot) return;

    // Get the clicked element within shadow DOM
    const path = event.composedPath();
    const clickedElement = path[0] as HTMLElement;
    
    // Check if click is within our shadow DOM
    if (!path.includes(this.elementRef.nativeElement)) {
      this.showUserMenu = false;
      return;
    }

    // Find menu-related elements
    const userInfo = shadowRoot.querySelector('.ufp-user-info');
    const userMenu = shadowRoot.querySelector('.ufp-user-menu');

    // If menu is open and click is outside both user info and menu, close it
    if (this.showUserMenu && 
        userInfo && userMenu && 
        !userInfo.contains(clickedElement) && 
        !userMenu.contains(clickedElement)) {
      this.showUserMenu = false;
    }
  }

  // Format selected date for header
  getFormattedSelectedDate(): string {
    const date = this.dates[this.selectedDateIndex];
    return `${date.dayName.toUpperCase()} ${date.dayNumber} ${date.monthName}`;
  }

  // Update the template binding for date selection
  onDateSelect(index: number) {
    if (this.selectedDateIndex === index) {
      return;
    }
    this.selectedDateIndex = index;
    this.loadClasses();
  }

  private loadBookedClasses() {
    this.bookedClassIds = this.scheduleService.getBookedClassIds();
  }

  isClassBooked(classId: string): boolean {
    return this.bookedClassIds.includes(classId);
  }

  onBookingConfirmationClose(result: boolean) {
    if (result) {
      // If booking was successful, refresh the booked classes
      this.scheduleService.fetchSchedule().subscribe(() => {
        this.loadBookedClasses();
      });
    }
  }

  private async handleClassBooking(classItem: ClassItem) {
    const subscriptionStatus = await firstValueFrom(
      this.subscriptionService.checkSubscriptionStatus(classItem)
    ) as SubscriptionCheckResponse;
    
    if (subscriptionStatus.canBook) {
      const tenantId = this.widgetConfig?.widget_enabled_tenants.find(
        t => t.tenant_uuid === this.selectedTenantUuid
      )?.tenant_id;

      const dialogRef = this.dialogService.open(BookingConfirmationComponent, {
        classItem,
        tenantUuid: this.selectedTenantUuid,
        tenantId,
        isWaitlist: false,
        androidAppLink: this.widgetConfig?.member_app_android_link || '',
        iosAppLink: this.widgetConfig?.member_app_ios_link || ''
      });

      const bookingComponent = dialogRef.instance as BookingConfirmationComponent;

      bookingComponent.bookingSuccess.subscribe((bookedClassId: string) => {
        this.bookedClassIds = [...this.bookedClassIds, bookedClassId];
        this.scheduleService.fetchSchedule().subscribe(() => {
          this.loadBookedClasses();
        });
      });
    } else {
      this.error = subscriptionStatus.message || this.getSubscriptionErrorMessage(classItem.behaviour);
    }
  }

  private startCountdownTimer(): void {
    this.countdownSubscription?.unsubscribe();

    // Run outside NgZone so the 1s tick does not keep the app unstable (NG0506).
    this.ngZone.runOutsideAngular(() => {
      this.countdownSubscription = interval(1000).subscribe(() => {
        if (!this.classes?.length) {
          return;
        }

        let changed = false;
        for (const classItem of this.classes) {
          const { isOpen, formattedTimeLeft } = this.utils.calculateTimeToBookingOpen(
            classItem.start_datetime,
            classItem.booking_opens_in_minutes,
          );

          const existing = this.bookingTimers[classItem.id];
          if (
            !existing ||
            existing.isOpen !== isOpen ||
            existing.timeLeft !== formattedTimeLeft
          ) {
            this.bookingTimers[classItem.id] = {
              isOpen,
              timeLeft: formattedTimeLeft,
            };
            changed = true;
          }
        }

        if (changed) {
          this.notifyViewUpdate();
        }
      });
    });
  }

  isBookingOpen(classItem: ClassItem): boolean {
    return this.bookingTimers[classItem.id]?.isOpen ?? false;
  }

  getBookingTimeLeft(classItem: ClassItem): string {
    return this.bookingTimers[classItem.id]?.timeLeft ?? '';
  }

  private async loadInitialData() {
    this.isLoadingStates = true;
    
    // Wait for both auth state and schedule to be loaded
    await Promise.all([
      firstValueFrom(this.authService.isLoggedIn$),
      this.scheduleService.fetchSchedule().toPromise()
    ]);

    this.loadBookedClasses();
    this.isLoadingStates = false;
    this.notifyViewUpdate();
  }

  showLoginDialog(): void {
    if (!this.companyUuid) {
      console.error('No company_uuid available for login');
      return;
    }

    const dialogRef = this.dialogService.open(LoginComponent, {
      enableSignup: this.widgetConfig?.enable_trial_booking_flow || false,
      companyUuid: this.companyUuid,
      preferredAuth: this.preferredAuth,
      customThemeVariables: this.getLoginThemeVariables(),
    });

    // Get the login component instance
    const loginComponent = dialogRef.instance as LoginComponent;
    
    // Subscribe to login success event
    loginComponent.loginSuccess.subscribe(() => {
      this.onLoginSuccess();
    });
  }
  private getLoginThemeVariables(): Record<string, string> {
    const primaryColor = this.utils.normalizeHexColor(
      this._customTheme.primaryColor || this.currentTheme.primaryColor,
    );

    return {
      '--ufp-primary-color': primaryColor,
      '--ufp-primary-color-hover': this.darkenColor(primaryColor, 10),
      '--ufp-background-color': this.utils.normalizeHexColor(
        this._customTheme.backgroundColor || this.currentTheme.dialogBackgroundColor,
      ),
      '--ufp-text-color': this.utils.normalizeHexColor(
        this._customTheme.textColor || this.currentTheme.dialogTextColor,
      ),
      '--ufp-text-muted': this.currentTheme.secondaryTextColor,
      '--ufp-border-color': this.currentTheme.dialogBorderColor,
      '--ufp-input-background': this.currentTheme.dialogBackgroundColor,
      '--ufp-primary-color-alpha': `${primaryColor}33`,
    };
  }

  private updateTheme() {
    // Set the current theme based on dark/light mode
    this.currentTheme = this._theme === 'dark' ? darkTheme : lightTheme;
    
    // Merge with custom theme properties
    this.currentTheme = { ...this.currentTheme, ...this._customTheme };
    
    // Apply theme variables to the host element
    Object.entries(this.currentTheme).forEach(([key, value]) => {
      this.elementRef.nativeElement.style.setProperty(`--${this.kebabCase(key)}`, value);
    });
  }

  private darkenColor(color: string, percent: number): string {
    const normalizedColor = this.utils.normalizeHexColor(color);
    const num = parseInt(normalizedColor.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  private kebabCase(str: string): string {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  openPoweredByLink(event: MouseEvent) {
    event.preventDefault();
    const referrerDomain = window.location.hostname;
    const url = new URL('https://www.fitnessforce.com');
    url.searchParams.set('campaign', 'classbookingwidget');
    url.searchParams.set('referrer', referrerDomain);
    window.open(url.toString(), '_blank');
  }

  isButtonDisabled(classItem: ClassItem): boolean {
    return this.isClassBooked(classItem.uuid) || 
           !this.isBookingOpen(classItem) || 
           (this.authService.isLoggedIn() && 
            classItem.subscriptionStatus?.canBook === false);
  }
}
